import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft, Building2, Loader2, Plus, Server, Trash2, ExternalLink,
  Mail, Phone, Send, MessageSquare, Users, ChevronDown, ChevronRight, GitCommit,
  Pencil, Paperclip, Ticket as TicketIcon, Sparkles, Lock, Unlock, Copy, Check,
  RotateCcw, UserPlus, Info, Globe, Handshake,
} from 'lucide-react';
import OrgNotesPanel from '../components/OrgNotesPanel';
import OrgTicketsTable from '../components/OrgTicketsTable';
import BitrixDocsCard from '../components/BitrixDocsCard';
import BitrixDealPicker from '../components/BitrixDealPicker';
import InstallationInventoryPanel from '../components/InstallationInventoryPanel';
import CmdbPanel from '../components/CmdbPanel';
import { useAuth } from '../lib/auth';
import OrgMaxChatCard from '../components/OrgMaxChatCard';
import OrgTgChatCard from '../components/OrgTgChatCard';
import OrgDigestsTab from '../components/OrgDigestsTab';
import OrgBitrixTab from '../components/OrgBitrixTab';
import { SslBadge } from '../components/SslBadge';
import { HealthBadge } from '../components/HealthBadge';
import { LastActivityWidget } from '../components/LastActivityWidget';
import { TicketCategoriesWidget } from '../components/TicketCategoriesWidget';
import OrgDialogsTab from '../components/OrgDialogsTab';
import AIChat from '../components/AIChat';
import { chatLinksApi, type ChatLinks } from '../api/chat_links';
import { slaApi, fmtMinutes, type SLAPlan } from '../api/sla';
import {
  orgsApi, clientsApi, type Organization, type Installation,
  type InstallationVersion, type ClientUser,
  CONTRACT_LABEL, CONTRACT_BADGE,
} from '../api/organizations';
import { ticketsApi, type TicketBrief } from '../api/tickets';
import { AttachmentsCard } from '../components/Attachments';
import { INSTALLATION_CATEGORIES } from '../api/attachments';
import { fmtTime, ticketNumber, STATUS_LABEL, STATUS_COLOR, PRIORITY_COLOR, PROJECT_LABEL, PROJECT_BADGE } from '../lib/format';
import { useOptions, findLabel, type OptionItem } from '../api/options';

export default function OrganizationDetail() {
  const { id = '' } = useParams();
  const { user } = useAuth();
  const isStaff = user?.type === 'staff';
  const [org, setOrg] = useState<Organization | null>(null);
  const [bitrixOpen, setBitrixOpen] = useState(false);
  const [installs, setInstalls] = useState<Installation[]>([]);
  const [tickets, setTickets] = useState<TicketBrief[]>([]);
  const [clients, setClients] = useState<ClientUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddInstall, setShowAddInstall] = useState(false);
  const [editInstall, setEditInstall] = useState<Installation | null>(null);
  const [showAddClient, setShowAddClient] = useState(false);
  const [hideClosedTickets, setHideClosedTickets] = useState<boolean>(() =>
    localStorage.getItem('orgDetail.hideClosed') !== '0'
  );
  const [editOrg, setEditOrg] = useState(false);
  const nav = useNavigate();
  type Tab = 'info' | 'tickets' | 'installations' | 'cmdb' | 'documents' | 'digests' | 'dialogs' | 'ai' | 'analytics' | 'bitrix';
  const [searchParams] = useSearchParams();
  const focusInstId = searchParams.get('inst');
  const [tab, setTab] = useState<Tab>(() => {
    // Если в URL есть ?inst=... — открываем сразу таб инсталляций
    if (focusInstId) return 'installations';
    return (localStorage.getItem('orgDetail.tab') as Tab) || 'tickets';
  });
  const switchTab = (t: Tab) => { setTab(t); localStorage.setItem('orgDetail.tab', t); };

  const load = async () => {
    setLoading(true);
    try {
      const [o, inst, tk, cl] = await Promise.all([
        orgsApi.get(id),
        orgsApi.installations(id),
        ticketsApi.list({ organization_id: id, limit: 200 }),
        clientsApi.list(id),
      ]);
      setOrg(o);
      setInstalls(inst);
      setTickets(tk.items);
      setClients(cl);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [id]);

  if (loading || !org) return <Loader2 className="animate-spin text-blue-400 mx-auto mt-10" />;

  return (
    <div>
      <Link to="/organizations" className="text-slate-400 hover:text-white text-sm mb-4 flex items-center gap-1">
        <ArrowLeft size={14} /> К списку
      </Link>

      {/* Заголовок + основные бейджи */}
      <div className="bg-slate-900 border border-slate-800 rounded-xl p-5 mb-4">
        <div className="flex items-start gap-4">
          <Building2 size={28} className="text-blue-400 mt-1" />
          <div className="flex-1">
            <div className="flex items-start justify-between gap-2 flex-wrap">
              <h1 className="text-2xl font-bold text-white">{org.name}</h1>
              <div className="flex gap-1.5 flex-wrap items-start">
                {/* Группа: contract / project_status (может переноситься) */}
                <div className="flex gap-1.5 flex-wrap">
                  {org.contract_active ? (
                    <>
                      <button onClick={async () => {
                        const cur = org.contract_ends_at ? new Date(org.contract_ends_at) : new Date();
                        cur.setFullYear(cur.getFullYear() + 1);
                        const def = cur.toISOString().slice(0, 10);
                        const v = window.prompt(`Новая дата окончания договора (YYYY-MM-DD):`, def);
                        if (!v) return;
                        if (!/^\d{4}-\d{2}-\d{2}$/.test(v)) { alert('Неверный формат'); return; }
                        try {
                          const { orgExtraApi } = await import('../api/v2');
                          await orgExtraApi.extendContract(id, v);
                          load();
                        } catch (e: any) { alert(e?.response?.data?.detail || 'Ошибка'); }
                      }} title="Продлить договор поддержки"
                        className="text-xs flex items-center gap-1 bg-emerald-700/30 hover:bg-emerald-700/60 text-emerald-200 px-2.5 py-1.5 rounded border border-emerald-800">
                        ✓ Продлить
                      </button>
                      <button onClick={async () => {
                        if (!window.confirm(`Снять «${org.name}» с поддержки? Договор станет неактивным, project_status → «нет поддержки», но дата окончания и история сохранятся.`)) return;
                        try {
                          await orgsApi.update(id, { contract_active: false, project_status: 'no_support' });
                          load();
                        } catch (e: any) { alert(e?.response?.data?.detail || 'Ошибка'); }
                      }} title="Снять с поддержки (contract_active=false, project_status=no_support)"
                        className="text-xs flex items-center gap-1 bg-slate-800 hover:bg-yellow-900/40 hover:text-yellow-300 text-slate-300 px-2.5 py-1.5 rounded">
                        ✕ Снять с поддержки
                      </button>
                    </>
                  ) : (
                    <button onClick={async () => {
                      if (!window.confirm(`Вернуть «${org.name}» на поддержку?`)) return;
                      try {
                        await orgsApi.update(id, { contract_active: true, project_status: 'support' });
                        load();
                      } catch (e: any) { alert(e?.response?.data?.detail || 'Ошибка'); }
                    }} title="Вернуть на поддержку (contract_active=true, project_status=support)"
                      className="text-xs flex items-center gap-1 bg-emerald-700/30 hover:bg-emerald-700/60 text-emerald-200 px-2.5 py-1.5 rounded border border-emerald-800">
                      ↻ Вернуть на поддержку
                    </button>
                  )}
                </div>
                {/* Группа: Изменить / Удалить — всегда вместе, не разрывается */}
                <div className="flex gap-1.5 flex-nowrap">
                  <button onClick={() => setEditOrg(true)}
                    className="text-xs flex items-center gap-1 bg-slate-800 hover:bg-slate-700 text-slate-200 px-2.5 py-1.5 rounded">
                    <Pencil size={12} /> Изменить
                  </button>
                  <button onClick={async () => {
                    if (!window.confirm(`Удалить организацию «${org.name}» вместе со всеми инсталляциями, привязками и дайджестами?\n\nДействие необратимо.`)) return;
                    try {
                      await orgsApi.remove(id);
                      nav('/organizations');
                    } catch (e: any) {
                      alert(e?.response?.data?.detail || 'Ошибка');
                    }
                  }} className="text-xs flex items-center gap-1 bg-slate-800 hover:bg-red-900/40 hover:text-red-300 text-slate-300 px-2.5 py-1.5 rounded">
                    <Trash2 size={12} /> Удалить
                  </button>
                </div>
              </div>
            </div>
            <div className="text-xs font-mono text-slate-500 mt-1">
              ИНН {org.inn}{org.kpp && ` / КПП ${org.kpp}`}
            </div>
            <div className="flex flex-wrap gap-2 mt-3">
              <span className={`inline-block px-2 py-0.5 rounded text-[11px] font-bold border ${PROJECT_BADGE[org.project_status]}`}>
                {PROJECT_LABEL[org.project_status]}
              </span>
              <span className={`inline-block px-2 py-0.5 rounded text-[11px] border ${CONTRACT_BADGE[org.contract_status]}`}>
                {CONTRACT_LABEL[org.contract_status]}
                {org.contract_days_left != null && org.contract_status === 'expiring_soon' && ` (${org.contract_days_left} дн)`}
              </span>
              {org.tariff && <span className="px-2 py-0.5 rounded text-[11px] bg-slate-800 text-slate-300 border border-slate-700">Тариф: {org.tariff}</span>}
              {org.contract_no && <span className="px-2 py-0.5 rounded text-[11px] bg-slate-800 text-slate-500 font-mono border border-slate-700">№ {org.contract_no}</span>}
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-4">
        <div className="space-y-4">
          {/* Табы — переключают центральную область */}
          <div className="flex border-b border-slate-800 -mb-px flex-wrap">
            <TabBtn active={tab === 'info'} onClick={() => switchTab('info')}
                    icon={<Info size={14} />} label="Информация" />
            <TabBtn active={tab === 'tickets'} onClick={() => switchTab('tickets')}
                    icon={<TicketIcon size={14} />} label="Тикеты" count={tickets.length} />
            <TabBtn active={tab === 'installations'} onClick={() => switchTab('installations')}
                    icon={<Server size={14} />} label="Инсталляции" count={installs.length} />
            <TabBtn active={tab === 'documents'} onClick={() => switchTab('documents')}
                    icon={<Paperclip size={14} />} label="Документы" />
            <TabBtn active={tab === 'bitrix'} onClick={() => switchTab('bitrix')}
                    icon={<ExternalLink size={14} />} label="Bitrix24" />
            <TabBtn active={tab === 'dialogs'} onClick={() => switchTab('dialogs')}
                    icon={<MessageSquare size={14} />} label="Диалоги" />
            <TabBtn active={tab === 'digests'} onClick={() => switchTab('digests')}
                    icon={<Sparkles size={14} />} label="Дайджесты" />
            <TabBtn active={tab === 'ai'} onClick={() => switchTab('ai')}
                    icon={<Sparkles size={14} />} label="Помощник" />
            <TabBtn active={tab === 'analytics'} onClick={() => switchTab('analytics')}
                    icon={<Sparkles size={14} />} label="Аналитика" />
            <TabBtn active={tab === 'cmdb'} onClick={() => switchTab('cmdb')}
                    icon={<Server size={14} />} label="CMDB" />
          </div>

          {tab === 'installations' && (
            <section className="bg-slate-900 border border-slate-800 rounded-xl">
              <header className="flex items-center justify-between px-5 py-3 border-b border-slate-800">
                <h2 className="font-bold text-white flex items-center gap-2">
                  <Server size={16} className="text-blue-400" /> Инсталляции
                </h2>
                <button onClick={() => setShowAddInstall(true)}
                  className="text-xs flex items-center gap-1 bg-blue-600 hover:bg-blue-500 text-white px-3 py-1.5 rounded">
                  <Plus size={12} /> Добавить
                </button>
              </header>
              <div className="p-3">
                {installs.length === 0 ? (
                  <div className="text-sm text-slate-500 text-center py-6">
                    Нет зарегистрированных инсталляций.
                    <button onClick={() => setShowAddInstall(true)} className="ml-2 text-blue-400 underline">Добавить первую</button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {installs.map(i => (
                      <InstallationRow
                        key={i.id} orgId={id} inst={i}
                        onChange={load}
                        onEdit={() => setEditInstall(i)}
                        defaultOpen={focusInstId === i.id}
                      />
                    ))}
                  </div>
                )}
              </div>
            </section>
          )}

          {tab === 'documents' && (
            <AttachmentsCard ownerType="organization" ownerId={id} withCategories />
          )}

          {tab === 'bitrix' && (
            <OrgBitrixTab orgId={id} />
          )}

          {tab === 'digests' && (
            <OrgDigestsTab orgId={id} />
          )}

          {tab === 'dialogs' && (
            <OrgDialogsTab orgId={id} />
          )}

          {tab === 'ai' && (
            <AIChat
              organizationId={id}
              storageKey={`ai-chat.org.${id}`}
              title={`AI-ассистент по ${org.name}`}
              subtitle="Ассистент отвечает на основе RAG-документации VALO. Если в документации нет ответа — задайте вопрос инженеру через тикет."
            />
          )}

          {tab === 'analytics' && <OrgAnalyticsTab orgId={id} />}

          {tab === 'info' && <OrgInfoTab org={org} onEdit={() => setEditOrg(true)} />}

          {tab === 'cmdb' && (
            <section className="bg-slate-900 border border-slate-800 rounded-xl">
              <header className="flex items-center justify-between px-5 py-3 border-b border-slate-800">
                <h2 className="font-bold text-white flex items-center gap-2">
                  <Server size={16} className="text-blue-400" /> CMDB — конфигурационные единицы
                </h2>
              </header>
              <div className="p-3">
                <CmdbPanel orgId={id} />
              </div>
            </section>
          )}

          {tab === 'tickets' && (
            <OrgTicketsTable
              tickets={tickets}
              hideClosed={hideClosedTickets}
              setHideClosed={(v) => {
                setHideClosedTickets(v);
                localStorage.setItem('orgDetail.hideClosed', v ? '1' : '0');
              }}
            />
          )}
        </div>

        {/* Сайдбар: контакты + договор + заметки */}
        <aside className="space-y-4">
          <Card title="Контакты">
            {org.contact_person && (
              <div>
                <div className="text-sm text-white font-medium">{org.contact_person}</div>
                {org.contact_position && <div className="text-xs text-slate-400">{org.contact_position}</div>}
              </div>
            )}
            <dl className="text-xs space-y-1.5 mt-2">
              {org.contact_phone && (
                <div className="flex items-center gap-2 text-slate-300">
                  <Phone size={11} className="text-slate-500" />
                  <span className="font-mono">{org.contact_phone}</span>
                  {org.contact_phone_ext && <span className="text-slate-500">доб. {org.contact_phone_ext}</span>}
                </div>
              )}
              {org.contact_email && (
                <a href={`mailto:${org.contact_email}`} className="flex items-center gap-2 text-blue-400 hover:underline">
                  <Mail size={11} className="text-slate-500" />
                  <span className="truncate">{org.contact_email}</span>
                </a>
              )}
              {org.contact_telegram && (
                <div className="flex items-center gap-2 text-slate-300">
                  <Send size={11} className="text-slate-500" />
                  <span className="font-mono">tg: {org.contact_telegram}</span>
                </div>
              )}
              {org.contact_max && (
                <div className="flex items-center gap-2 text-slate-300">
                  <MessageSquare size={11} className="text-slate-500" />
                  <span className="font-mono">max: {org.contact_max}</span>
                </div>
              )}
            </dl>
            <div className="mt-3 pt-3 border-t border-slate-800">
              <ChatLinksRow orgId={id} />
            </div>
            {!org.contact_person && !org.contact_phone && !org.contact_email
              && <div className="text-xs text-slate-600">не указаны</div>}
          </Card>

          <OrgMaxChatCard orgId={id} />
          <OrgTgChatCard orgId={id} />

          <Card title={<span className="flex items-center gap-2"><Users size={12} /> Аккаунты SD ({clients.length})</span>}>
            <p className="text-[10px] text-slate-600 mb-2">
              Учётные записи для входа клиента в Service Desk
              (отдельно от контактного лица в карточке).
            </p>
            {clients.length === 0 ? (
              <div className="text-xs text-slate-500 mb-2">Нет аккаунтов SD</div>
            ) : (
              <ul className="text-xs space-y-1.5 mb-2">
                {clients.map(c => <ClientAccountRow key={c.id} c={c} orgId={id} onChanged={load} />)}
              </ul>
            )}
            <button onClick={() => setShowAddClient(true)}
              className="text-xs flex items-center gap-1 bg-blue-600 hover:bg-blue-500 text-white px-3 py-1.5 rounded w-full justify-center">
              <Plus size={11} /> Создать аккаунт SD
            </button>
          </Card>

          <SLACard orgId={id} />

          <Card title="Договор">
            <dl className="text-xs space-y-1.5 text-slate-400">
              {org.contract_no && <div className="flex justify-between"><dt>№</dt><dd className="text-slate-300 font-mono">{org.contract_no}</dd></div>}
              {org.contract_started_at && <div className="flex justify-between"><dt>Начало</dt><dd className="text-slate-300">{org.contract_started_at}</dd></div>}
              {org.contract_ends_at && <div className="flex justify-between"><dt>Окончание</dt><dd className={org.contract_status === 'expired' ? 'text-red-400' : org.contract_status === 'expiring_soon' ? 'text-yellow-400' : 'text-slate-300'}>{org.contract_ends_at}</dd></div>}
              {org.tariff && <div className="flex justify-between"><dt>Тариф</dt><dd className="text-slate-300">{org.tariff}</dd></div>}
            </dl>
          </Card>

          {org.legal_address && (
            <Card title="Юр. адрес">
              <p className="text-sm text-slate-300">{org.legal_address}</p>
            </Card>
          )}

          {org.notes && (
            <Card title="Краткое описание">
              <p className="text-sm text-slate-300 whitespace-pre-wrap">{org.notes}</p>
            </Card>
          )}

          {isStaff && (
            <Card title="Bitrix24">
              {org.bitrix_deal_url ? (
                <a href={org.bitrix_deal_url} target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-sm text-blue-400 hover:underline break-all">
                  Сделка в CRM ↗
                </a>
              ) : (
                <div className="text-xs text-slate-500">Сделка не привязана.</div>
              )}
              <button onClick={() => setBitrixOpen(true)}
                className="mt-2 inline-flex items-center gap-1 text-[11px] bg-slate-800 hover:bg-slate-700 text-white px-2 py-1 rounded">
                Найти по ИНН {org.inn}
              </button>
            </Card>
          )}

          {isStaff && (
            <Card title="Battlecard">
              <div className="space-y-1">
                <a href={`https://battlecard.devkz.ru/customers?q=${org.inn}`}
                   target="_blank" rel="noopener noreferrer"
                   className="block text-sm text-blue-400 hover:underline">
                  Заказчик ↗
                </a>
                <a href={`https://battlecard.devkz.ru/pilots?q=${encodeURIComponent(org.name)}`}
                   target="_blank" rel="noopener noreferrer"
                   className="block text-sm text-blue-400 hover:underline">
                  Пилоты ↗
                </a>
                <a href={`https://battlecard.devkz.ru/installations?q=${encodeURIComponent(org.name)}`}
                   target="_blank" rel="noopener noreferrer"
                   className="block text-sm text-blue-400 hover:underline">
                  Инсталляции ↗
                </a>
                <div className="text-[10px] text-slate-600 pt-1">
                  Прямой переход в battlecard по этой организации.
                </div>
              </div>
            </Card>
          )}

          {isStaff && org.bitrix_deal_url && (
            <BitrixDocsCard orgId={id} />
          )}

          {isStaff && (
            <Card title="Заметки сотрудников">
              <OrgNotesPanel orgId={id} />
            </Card>
          )}
        </aside>
      </div>

      {showAddInstall && (
        <InstallationFormModal
          orgId={id}
          onClose={() => setShowAddInstall(false)}
          onSaved={() => { setShowAddInstall(false); load(); }}
        />
      )}
      {editOrg && (
        <OrgEditModal org={org}
          onClose={() => setEditOrg(false)}
          onSaved={() => { setEditOrg(false); load(); }}
        />
      )}
      {bitrixOpen && (
        <BitrixDealPicker
          orgId={id} inn={org.inn} currentUrl={org.bitrix_deal_url}
          onClose={() => setBitrixOpen(false)}
          onSaved={() => { setBitrixOpen(false); load(); }}
        />
      )}
      {editInstall && (
        <InstallationFormModal
          orgId={id} initial={editInstall}
          onClose={() => setEditInstall(null)}
          onSaved={() => { setEditInstall(null); load(); }}
        />
      )}
      {showAddClient && (
        <AddClientModal orgId={id} onClose={() => setShowAddClient(false)} onCreated={() => { setShowAddClient(false); load(); }} />
      )}
    </div>
  );
}

// ── Раскрываемая строка инсталляции (анкета + версии + вложения) ──────────────
function InstallationRow({
  orgId, inst, onChange, onEdit, defaultOpen = false,
}: { orgId: string; inst: Installation; onChange: () => void; onEdit: () => void; defaultOpen?: boolean }) {
  const [open, setOpen] = useState(defaultOpen);
  const [versions, setVersions] = useState<InstallationVersion[]>([]);
  const [loading, setLoading] = useState(false);
  const [showAddVersion, setShowAddVersion] = useState(false);

  const { items: instTypeOpts } = useOptions('installation_type');

  const loadVersions = async () => {
    setLoading(true);
    try { setVersions(await orgsApi.versions(orgId, inst.id)); }
    finally { setLoading(false); }
  };
  useEffect(() => { if (open) loadVersions(); }, [open]);

  // versions отсортированы DESC по installed_at — берём первую (latest) на компонент
  const latestByComponent = versions.reduce<Record<string, InstallationVersion>>((acc, v) => {
    if (!acc[v.component]) acc[v.component] = v;
    return acc;
  }, {});

  const hasAnyQuestionnaire = !!(
    inst.installation_type || inst.os || inst.office_package || inst.dlp ||
    inst.antivirus || inst.sandbox || inst.siem || inst.storage_type ||
    inst.auth_type || inst.license_users_named || inst.license_users_concurrent ||
    inst.license_contractors ||
    (inst.license_options && Object.keys(inst.license_options).length)
  );

  return (
    <div className={`bg-slate-950/50 border rounded-lg overflow-hidden ${
      defaultOpen ? 'border-blue-600 ring-1 ring-blue-600/40' : 'border-slate-800'
    }`}>
      <header className="flex items-center gap-2 px-3 py-2 hover:bg-slate-800/40 cursor-pointer" onClick={() => setOpen(o => !o)}>
        {open ? <ChevronDown size={14} className="text-slate-500" /> : <ChevronRight size={14} className="text-slate-500" />}
        <div className="flex-1 grid grid-cols-[1fr_2fr_2fr_auto_auto_auto] gap-3 items-center">
          <span className="text-white font-medium text-sm">
            {inst.name}
            {inst.installation_type && (
              <span className="ml-2 text-[10px] text-slate-500 font-normal">
                {findLabel(instTypeOpts, inst.installation_type)}
              </span>
            )}
          </span>
          <span className="text-slate-300 text-sm">
            {inst.product}{inst.version && <span className="text-slate-500 ml-2 font-mono">{inst.version}</span>}
          </span>
          <span className="text-xs inline-flex items-center gap-1.5 flex-wrap">
            {inst.host_url ? (
              <a href={inst.host_url} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()}
                 className="text-blue-400 hover:underline inline-flex items-center gap-1">
                {inst.host_url.replace(/^https?:\/\//, '').slice(0, 40)} <ExternalLink size={9} />
              </a>
            ) : <span className="text-slate-600">—</span>}
            {inst.host_url && (
              <SslBadge inst={inst} orgId={orgId} onChanged={onChange} />
            )}
          </span>
          {/* Статус инсталляции = toggle-кнопки (бейдж убран — он дублировал
              активную кнопку). Активная подсвечивается ярким цветом. */}
          <div className="flex gap-0.5 ml-1" onClick={e => e.stopPropagation()}>
            <button
              onClick={async () => {
                const cur = inst.support_override;
                const next = cur === 'pilot' ? null : 'pilot';
                await orgsApi.updateInstall(orgId, inst.id, { support_override: next as any });
                onChange();
              }}
              title={inst.support_override === 'pilot' ? 'Снять статус «Пилот»' : 'Сделать пилотом'}
              className={`text-[9px] uppercase font-bold px-1.5 py-0.5 rounded border ${
                inst.support_override === 'pilot'
                  ? 'bg-purple-700 border-purple-600 text-white'
                  : 'bg-slate-900 border-slate-700 text-slate-400 hover:text-purple-300 hover:border-purple-700'
              }`}>
              Пилот
            </button>
            <button
              onClick={async () => {
                const cur = inst.support_override;
                const next = cur === 'support' ? null : 'support';
                await orgsApi.updateInstall(orgId, inst.id, { support_override: next as any });
                onChange();
              }}
              title={inst.support_override === 'support' ? 'Снять переопределение' : 'Сделать «Поддержка»'}
              className={`text-[9px] uppercase font-bold px-1.5 py-0.5 rounded border ${
                inst.support_override === 'support'
                  ? 'bg-emerald-700 border-emerald-600 text-white'
                  : 'bg-slate-900 border-slate-700 text-slate-400 hover:text-emerald-300 hover:border-emerald-700'
              }`}>
              Поддержка
            </button>
            {!inst.support_override && (
              <span className="text-[10px] text-slate-600 ml-1 self-center"
                    title="Статус наследуется от организации">наследует</span>
            )}
          </div>
          <button onClick={(e) => { e.stopPropagation(); onEdit(); }}
                  title="Редактировать"
                  className="text-slate-500 hover:text-blue-400 p-1">
            <Pencil size={12} />
          </button>
          <button onClick={async (e) => {
            e.stopPropagation();
            if (window.confirm(`Удалить инсталляцию «${inst.name}»? Все версии и документы тоже удалятся.`)) {
              await orgsApi.deleteInstall(orgId, inst.id); onChange();
            }
          }} className="text-slate-500 hover:text-red-400 p-1" title="Удалить">
            <Trash2 size={12} />
          </button>
        </div>
      </header>

      {open && (
        <div className="border-t border-slate-800 p-3 bg-slate-950/30 space-y-4">
          <div>
            <h4 className="text-xs uppercase tracking-wider text-slate-500 mb-2">Анкета инсталляции</h4>
            {hasAnyQuestionnaire ? (
              <QuestionnaireSummary inst={inst} />
            ) : (
              <div className="text-xs text-slate-600 italic">
                Анкета не заполнена — нажми «карандаш» наверху, чтобы внести данные.
              </div>
            )}
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-xs uppercase tracking-wider text-slate-500 flex items-center gap-1.5">
                <GitCommit size={11} /> Версии компонентов ({versions.length})
              </h4>
              <button onClick={() => setShowAddVersion(true)}
                className="text-[11px] bg-slate-800 hover:bg-slate-700 text-slate-300 px-2 py-1 rounded inline-flex items-center gap-1">
                <Plus size={10} /> Зафиксировать версию
              </button>
            </div>
            {loading ? <Loader2 className="animate-spin text-blue-400" /> : versions.length === 0 ? (
              <div className="text-xs text-slate-500 py-2">Нет данных. Зафиксируй текущую версию backend/frontend для истории.</div>
            ) : (
              <table className="w-full text-xs">
                <thead className="text-[10px] text-slate-600 uppercase tracking-wider">
                  <tr>
                    <th className="text-left py-1.5 px-2">Компонент</th>
                    <th className="text-left py-1.5 px-2">Версия</th>
                    <th className="text-left py-1.5 px-2">Branch / commit</th>
                    <th className="text-left py-1.5 px-2">Установлено</th>
                    <th className="text-right py-1.5 px-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {versions.map(v => {
                    const isLatest = latestByComponent[v.component]?.id === v.id;
                    return (
                      <tr key={v.id} className={`border-t border-slate-800 ${isLatest ? '' : 'opacity-60'}`}>
                        <td className="py-1.5 px-2">
                          <span className="font-mono text-slate-200">{v.component}</span>
                          {isLatest && <span className="ml-2 text-[9px] text-green-400 uppercase">текущая</span>}
                        </td>
                        <td className="py-1.5 px-2 font-mono text-slate-300">
                          {v.current || v.base || v.raw_text || '—'}
                        </td>
                        <td className="py-1.5 px-2 font-mono text-slate-400 text-[11px]">
                          {v.branch && <span>{v.branch}</span>}
                          {v.commit_sha && <span className="ml-1 text-slate-500">@ {v.commit_sha.slice(0, 8)}</span>}
                          {v.commit_title && <div className="text-slate-600 truncate max-w-md">{v.commit_title}</div>}
                        </td>
                        <td className="py-1.5 px-2 text-slate-500">{fmtTime(v.installed_at)}</td>
                        <td className="py-1.5 px-2 text-right">
                          <button onClick={async () => {
                            if (window.confirm('Удалить запись о версии?')) {
                              await orgsApi.deleteVersion(orgId, inst.id, v.id);
                              loadVersions();
                            }
                          }} className="text-slate-500 hover:text-red-400 p-1">
                            <Trash2 size={11} />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
            {showAddVersion && <AddVersionModal orgId={orgId} instId={inst.id}
              onClose={() => setShowAddVersion(false)}
              onCreated={() => { setShowAddVersion(false); loadVersions(); }} />}
          </div>

          <div>
            <h4 className="text-xs uppercase tracking-wider text-slate-500 mb-2">
              Документы инсталляции
            </h4>
            <AttachmentsCard
              ownerType="installation" ownerId={inst.id}
              withCategories categories={INSTALLATION_CATEGORIES}
              title="Паспорт системы / схема сети / ..."
            />
          </div>

          <div>
            <h4 className="text-xs uppercase tracking-wider text-slate-500 mb-2">
              Опись инсталляции
            </h4>
            <InstallationInventoryPanel instId={inst.id} />
          </div>
        </div>
      )}
    </div>
  );
}

// ── Сводка анкеты ────────────────────────────────────────────────────────────
function QuestionnaireSummary({ inst }: { inst: Installation }) {
  const types = useOptions('installation_type').items;
  const oss = useOptions('os').items;
  const offices = useOptions('office_package').items;
  const dlps = useOptions('dlp').items;
  const avs = useOptions('antivirus').items;
  const sbs = useOptions('sandbox').items;
  const siems = useOptions('siem').items;
  const storages = useOptions('storage_type').items;
  const auths = useOptions('auth_type').items;
  const ldaps = useOptions('auth_ldap').items;
  const ssos = useOptions('auth_sso').items;
  const lic = useOptions('license_option').items;

  const rows: Array<[string, React.ReactNode]> = [];
  const push = (label: string, value: React.ReactNode) => {
    if (value !== null && value !== undefined && value !== '') rows.push([label, value]);
  };

  push('Топология', inst.installation_type ? findLabel(types, inst.installation_type) : null);
  push('ОС сервера', inst.os ? findLabel(oss, inst.os) : null);
  push('Офис-пакет', inst.office_package ? findLabel(offices, inst.office_package) : null);
  push('DLP', inst.dlp ? findLabel(dlps, inst.dlp) : null);
  push('Антивирус', inst.antivirus ? findLabel(avs, inst.antivirus) : null);
  push('Sandbox', inst.sandbox ? findLabel(sbs, inst.sandbox) : null);
  push('SIEM', inst.siem ? findLabel(siems, inst.siem) : null);
  push('Хранилище', inst.storage_type ? findLabel(storages, inst.storage_type) : null);
  if (inst.auth_type) {
    const provider = inst.auth_provider
      ? (inst.auth_type === 'ldap' ? findLabel(ldaps, inst.auth_provider)
        : inst.auth_type === 'sso' ? findLabel(ssos, inst.auth_provider)
        : inst.auth_provider)
      : null;
    push('Аутентификация',
      <span>
        {findLabel(auths, inst.auth_type)}
        {provider && <span className="text-slate-500"> — {provider}</span>}
      </span>);
  }
  push('Лицензия (named)', inst.license_users_named);
  push('Лицензия (concurrent)', inst.license_users_concurrent);
  push('Подрядчики', inst.license_contractors);

  // Опции лицензии: показываем только те, где значение truthy/задано.
  const licValues = inst.license_options || {};
  const activeLic = lic.filter(opt => {
    const v = licValues[opt.value];
    if (opt.value_type === 'number') return typeof v === 'number';
    return v === true;
  });
  if (activeLic.length) {
    push('Опции лицензии',
      <div className="flex flex-wrap gap-1 justify-end">
        {activeLic.map(opt => (
          <span key={opt.id}
            className="px-1.5 py-0.5 rounded bg-slate-800 text-[10px] text-slate-200 border border-slate-700">
            {opt.label}
            {opt.value_type === 'number' && (
              <span className="text-blue-300 font-mono ml-1">= {String(licValues[opt.value])}</span>
            )}
          </span>
        ))}
      </div>
    );
  }

  if (rows.length === 0) return null;

  return (
    <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1.5 text-xs">
      {rows.map(([label, value]) => (
        <div key={label} className="flex justify-between gap-3 border-b border-slate-800/60 pb-1">
          <dt className="text-slate-500 shrink-0">{label}</dt>
          <dd className="text-slate-200 text-right">{value}</dd>
        </div>
      ))}
    </dl>
  );
}

// ── Создание / редактирование инсталляции (полная анкета) ────────────────────
type InstForm = {
  name: string; product: string; version: string; host_url: string; notes: string;
  deployed_at: string; support_override: '' | 'support' | 'pilot' | 'no_support';
  installation_type: string;
  os: string;
  office_package: string;
  dlp: string;
  antivirus: string;
  sandbox: string;
  siem: string;
  storage_type: string;
  auth_type: string;
  auth_provider: string;
  license_users_named: string;
  license_users_concurrent: string;
  license_contractors: string;
  license_options: Record<string, boolean | number>;
  // Pilot lifecycle (v0.9.36)
  pilot_started_at: string;
  pilot_planned_end_at: string;
  pilot_phase: '' | 'poc' | 'trial' | 'decision';
  pilot_notes: string;
};

// Стандартные имена инсталляций (v0.9.50): Test = пилот, Prod = продакшн.
// Не используем имя организации — оно бесполезно когда у одного клиента
// несколько инсталляций.
const _DEFAULT_NAMES = ['Prod', 'Test', 'Основная', ''];

const EMPTY_FORM: InstForm = {
  name: 'Prod', product: 'VALO Cloud', version: '', host_url: '', notes: '',
  deployed_at: '', support_override: '',
  installation_type: '', os: '', office_package: '', dlp: '', antivirus: '', sandbox: '',
  siem: '', storage_type: '', auth_type: '', auth_provider: '',
  license_users_named: '', license_users_concurrent: '', license_contractors: '',
  license_options: {},
  pilot_started_at: '', pilot_planned_end_at: '', pilot_phase: '', pilot_notes: '',
};

function InstallationFormModal({
  orgId, initial, onClose, onSaved,
}: { orgId: string; initial?: Installation; onClose: () => void; onSaved: () => void }) {
  const isEdit = !!initial;
  const [form, setForm] = useState<InstForm>(() => initial ? {
    name: initial.name || 'Prod',
    product: initial.product || 'VALO Cloud',
    version: initial.version || '',
    host_url: initial.host_url || '',
    notes: initial.notes || '',
    deployed_at: initial.deployed_at || '',
    support_override: (initial.support_override as any) || '',
    installation_type: initial.installation_type || '',
    os: initial.os || '',
    office_package: initial.office_package || '',
    dlp: initial.dlp || '',
    antivirus: initial.antivirus || '',
    sandbox: initial.sandbox || '',
    siem: initial.siem || '',
    storage_type: initial.storage_type || '',
    auth_type: initial.auth_type || '',
    auth_provider: initial.auth_provider || '',
    license_users_named: initial.license_users_named?.toString() ?? '',
    license_users_concurrent: initial.license_users_concurrent?.toString() ?? '',
    license_contractors: initial.license_contractors?.toString() ?? '',
    license_options: initial.license_options || {},
    pilot_started_at: (initial as any).pilot_started_at || '',
    pilot_planned_end_at: (initial as any).pilot_planned_end_at || '',
    pilot_phase: ((initial as any).pilot_phase as InstForm['pilot_phase']) || '',
    pilot_notes: (initial as any).pilot_notes || '',
  } : EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const upd = (k: keyof InstForm, v: any) => {
    setForm(f => {
      const next = { ...f, [k]: v };
      // Автоматическое имя по статусу (v0.9.50). Срабатывает только если
      // пользователь ещё не редактировал имя руками (оно содержит одно из
      // дефолтных значений) и только при создании.
      if (!isEdit && k === 'support_override' && _DEFAULT_NAMES.includes(f.name)) {
        next.name = v === 'pilot' ? 'Test' : 'Prod';
      }
      return next;
    });
  };
  const setAuthType = (v: string) => setForm(f => ({ ...f, auth_type: v, auth_provider: '' }));

  // Все справочники для формы
  const instTypeOpts = useOptions('installation_type').items;
  const osOpts = useOptions('os').items;
  const officeOpts = useOptions('office_package').items;
  const dlpOpts = useOptions('dlp').items;
  const avOpts = useOptions('antivirus').items;
  const sbOpts = useOptions('sandbox').items;
  const siemOpts = useOptions('siem').items;
  const storageOpts = useOptions('storage_type').items;
  const authTypeOpts = useOptions('auth_type').items;
  const ldapOpts = useOptions('auth_ldap').items;
  const ssoOpts = useOptions('auth_sso').items;
  const licOpts = useOptions('license_option').items;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true); setErr(null);
    try {
      const payload: any = {
        name: form.name || undefined,
        product: form.product || undefined,
        version: form.version || null,
        host_url: form.host_url || null,
        notes: form.notes || null,
        deployed_at: form.deployed_at || null,
        support_override: form.support_override || null,
        installation_type: form.installation_type || null,
        os: form.os || null,
        office_package: form.office_package || null,
        dlp: form.dlp || null,
        antivirus: form.antivirus || null,
        sandbox: form.sandbox || null,
        siem: form.siem || null,
        storage_type: form.storage_type || null,
        auth_type: form.auth_type || null,
        auth_provider: form.auth_provider || null,
        license_users_named: form.license_users_named ? Number(form.license_users_named) : null,
        license_users_concurrent: form.license_users_concurrent ? Number(form.license_users_concurrent) : null,
        license_contractors: form.license_contractors ? Number(form.license_contractors) : null,
        license_options: form.license_options,
        pilot_started_at: form.pilot_started_at || null,
        pilot_planned_end_at: form.pilot_planned_end_at || null,
        pilot_phase: form.pilot_phase || null,
        pilot_notes: form.pilot_notes || null,
      };
      if (isEdit) {
        await orgsApi.updateInstall(orgId, initial!.id, payload);
      } else {
        Object.keys(payload).forEach(k => payload[k] === undefined && delete payload[k]);
        await orgsApi.createInstall(orgId, payload);
      }
      onSaved();
    } catch (e: any) {
      setErr(e?.response?.data?.detail || e?.message || 'Ошибка');
    } finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-start justify-center z-50 overflow-y-auto py-8">
      <form onSubmit={submit}
            className="bg-slate-900 border border-slate-700 rounded-xl w-full max-w-3xl mx-4 shadow-2xl">
        <div className="p-4 border-b border-slate-800 flex justify-between sticky top-0 bg-slate-900 z-10">
          <h3 className="text-lg font-bold text-white">
            {isEdit ? `Редактировать инсталляцию: ${initial!.name}` : 'Новая инсталляция'}
          </h3>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-white">✕</button>
        </div>

        <div className="p-5 space-y-5">
          <Section title="Основное">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Название">
                <Inp v={form.name} k="name" upd={upd} />
                <div className="text-[10px] text-slate-500 mt-0.5">
                  Test — для пилотов, Prod — для продакшна. Имя клиента в названии не нужно.
                </div>
              </Field>
              <Field label="Продукт"><Inp v={form.product} k="product" upd={upd} /></Field>
              <Field label="Версия"><Inp v={form.version} k="version" upd={upd} placeholder="v3.5.2" /></Field>
              <Field label="URL"><Inp v={form.host_url} k="host_url" upd={upd} placeholder="https://customer.example.ru" /></Field>
              <Field label="Дата установки"><Inp v={form.deployed_at} k="deployed_at" upd={upd} type="date" /></Field>
              <Field label="Override проектного статуса">
                <SelectStatic v={form.support_override} onChange={v => upd('support_override', v)}
                  options={[
                    { value: '', label: '— наследует от организации —' },
                    { value: 'support', label: 'Поддержка' },
                    { value: 'pilot', label: 'Пилот' },
                    { value: 'no_support', label: 'Нет поддержки' },
                  ]} />
              </Field>
            </div>
            <Field label="Заметки">
              <textarea value={form.notes} onChange={e => upd('notes', e.target.value)} rows={2}
                className="w-full bg-slate-950 border border-slate-700 rounded px-3 py-1.5 text-sm text-white focus:border-blue-500 outline-none" />
            </Field>
          </Section>

          <Section title="Пилот — жизненный цикл">
            <div className="text-xs text-slate-500 mb-2">
              Используется для уведомлений (за 7/3/1/0 дней до план-даты) и
              реестра пилотов <Link to="/pilots" className="text-blue-300 hover:underline">/pilots</Link>.
              Решения convert/cancel/extend — там же. Поля можно заполнить
              даже когда инсталляция в режиме «Поддержка» — пригодится если
              позже переключите её в пилот.
            </div>
            <div className="grid grid-cols-3 gap-3">
              <Field label="Дата старта пилота">
                <Inp v={form.pilot_started_at} k="pilot_started_at" upd={upd} type="date" />
              </Field>
              <Field label="План окончания">
                <Inp v={form.pilot_planned_end_at} k="pilot_planned_end_at" upd={upd} type="date" />
              </Field>
              <Field label="Фаза">
                <SelectStatic v={form.pilot_phase} onChange={v => upd('pilot_phase', v)}
                  options={[
                    { value: '', label: '— не указана —' },
                    { value: 'poc', label: 'PoC (proof of concept)' },
                    { value: 'trial', label: 'Trial (полный пилот)' },
                    { value: 'decision', label: 'Решение' },
                  ]} />
              </Field>
            </div>
            <Field label="Заметки PM (история пилота)">
              <textarea value={form.pilot_notes} onChange={e => upd('pilot_notes', e.target.value)} rows={3}
                className="w-full bg-slate-950 border border-slate-700 rounded px-3 py-1.5 text-sm text-white focus:border-blue-500 outline-none"
                placeholder="Цели пилота, заинтересованные лица, критерии успеха…" />
            </Field>
          </Section>

          <Section title="Инфраструктура">
            <Field label="Тип развёртывания (топология)">
              <RadioGroup name="installation_type" value={form.installation_type}
                onChange={v => upd('installation_type', v)} options={instTypeOpts}
                allowEmpty="— не указано —" />
            </Field>
            <Field label="ОС сервера">
              <SelectDyn v={form.os} onChange={v => upd('os', v)} options={osOpts} />
            </Field>
          </Section>

          <Section title="Интеграции">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Офисный пакет">
                <SelectDyn v={form.office_package} onChange={v => upd('office_package', v)} options={officeOpts} />
              </Field>
              <Field label="DLP">
                <SelectDyn v={form.dlp} onChange={v => upd('dlp', v)} options={dlpOpts} />
              </Field>
              <Field label="Антивирус">
                <SelectDyn v={form.antivirus} onChange={v => upd('antivirus', v)} options={avOpts} />
              </Field>
              <Field label="Sandbox / песочница">
                <SelectDyn v={form.sandbox} onChange={v => upd('sandbox', v)} options={sbOpts} />
              </Field>
              <Field label="SIEM">
                <SelectDyn v={form.siem} onChange={v => upd('siem', v)} options={siemOpts} />
              </Field>
              <Field label="Хранилище">
                <SelectDyn v={form.storage_type} onChange={v => upd('storage_type', v)} options={storageOpts} />
              </Field>
            </div>
          </Section>

          <Section title="Аутентификация">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Тип">
                <SelectDyn v={form.auth_type} onChange={setAuthType} options={authTypeOpts} />
              </Field>
              {form.auth_type === 'ldap' && (
                <Field label="LDAP-каталог">
                  <SelectDyn v={form.auth_provider} onChange={v => upd('auth_provider', v)} options={ldapOpts} />
                </Field>
              )}
              {form.auth_type === 'sso' && (
                <Field label="SSO-провайдер">
                  <SelectDyn v={form.auth_provider} onChange={v => upd('auth_provider', v)} options={ssoOpts} />
                </Field>
              )}
            </div>
          </Section>

          <Section title="Лицензия — пользователи">
            <div className="grid grid-cols-3 gap-3">
              <Field label="Named-пользователи">
                <Inp v={form.license_users_named} k="license_users_named" upd={upd} type="number" min={0} />
              </Field>
              <Field label="Concurrent-пользователи">
                <Inp v={form.license_users_concurrent} k="license_users_concurrent" upd={upd} type="number" min={0} />
              </Field>
              <Field label="Подрядчики">
                <Inp v={form.license_contractors} k="license_contractors" upd={upd} type="number" min={0} />
              </Field>
            </div>
          </Section>

          <Section title="Лицензия — опции">
            {licOpts.length === 0 ? (
              <p className="text-xs text-slate-500">
                Справочник «Опции лицензии» пуст. Добавь записи в разделе «Настройки».
              </p>
            ) : (
              <LicenseOptionsEditor
                opts={licOpts}
                values={form.license_options}
                onChange={v => upd('license_options', v)}
              />
            )}
          </Section>
        </div>

        {err && <div className="bg-red-900/30 border-y border-red-800 text-red-300 px-4 py-2 text-sm">{err}</div>}
        <div className="p-4 flex justify-end gap-2 border-t border-slate-800 sticky bottom-0 bg-slate-900">
          <button type="button" onClick={onClose} className="px-4 py-2 bg-slate-800 hover:bg-slate-700 rounded text-slate-300 text-sm">Отмена</button>
          <button type="submit" disabled={submitting}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-500 disabled:bg-slate-700 rounded text-white text-sm font-bold">
            {submitting ? 'Сохраняю…' : (isEdit ? 'Сохранить' : 'Создать')}
          </button>
        </div>
      </form>
    </div>
  );
}

// ── Опции лицензии: чекбоксы (bool) + числовые поля (number) ─────────────────
function LicenseOptionsEditor({
  opts, values, onChange,
}: {
  opts: OptionItem[];
  values: Record<string, boolean | number>;
  onChange: (v: Record<string, boolean | number>) => void;
}) {
  const setVal = (k: string, v: boolean | number | undefined) => {
    const next = { ...values };
    if (v === undefined || v === false) delete next[k];
    else next[k] = v;
    onChange(next);
  };

  // Группируем: сначала числовые квоты, потом булевы.
  const nums = opts.filter(o => o.value_type === 'number');
  const bools = opts.filter(o => o.value_type !== 'number');

  return (
    <div className="space-y-4">
      {nums.length > 0 && (
        <div>
          <h5 className="text-[10px] uppercase tracking-wider text-slate-500 mb-2">Квоты</h5>
          <div className="grid grid-cols-2 gap-3">
            {nums.map(opt => (
              <Field key={opt.id} label={opt.label}>
                <input
                  type="number" min={0} value={values[opt.value] as number ?? ''}
                  onChange={e => setVal(opt.value, e.target.value === '' ? undefined : Number(e.target.value))}
                  placeholder="—"
                  className="w-full bg-slate-950 border border-slate-700 rounded px-3 py-1.5 text-sm text-white focus:border-blue-500 outline-none" />
              </Field>
            ))}
          </div>
        </div>
      )}
      {bools.length > 0 && (
        <div>
          <h5 className="text-[10px] uppercase tracking-wider text-slate-500 mb-2">Возможности</h5>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-y-1.5 gap-x-4">
            {bools.map(opt => (
              <label key={opt.id} className="flex items-start gap-2 cursor-pointer text-sm text-slate-200 py-1">
                <input type="checkbox" checked={!!values[opt.value]}
                       onChange={e => setVal(opt.value, e.target.checked || undefined)}
                       className="mt-1 accent-blue-500" />
                <span>
                  {opt.label}
                  {opt.hint && <span className="block text-[11px] text-slate-500 mt-0.5">{opt.hint}</span>}
                </span>
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Утилиты для формы ────────────────────────────────────────────────────────
function SelectStatic({
  v, onChange, options,
}: { v: string; onChange: (v: string) => void; options: Array<{ value: string; label: string }> }) {
  return (
    <select value={v} onChange={e => onChange(e.target.value)}
      className="w-full bg-slate-950 border border-slate-700 rounded px-3 py-1.5 text-sm text-white focus:border-blue-500 outline-none">
      {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  );
}

function SelectDyn({
  v, onChange, options, emptyLabel = '— не указано —',
}: { v: string; onChange: (v: string) => void; options: OptionItem[]; emptyLabel?: string }) {
  return (
    <select value={v} onChange={e => onChange(e.target.value)}
      className="w-full bg-slate-950 border border-slate-700 rounded px-3 py-1.5 text-sm text-white focus:border-blue-500 outline-none">
      <option value="">{emptyLabel}</option>
      {options.map(o => <option key={o.id} value={o.value}>{o.label}</option>)}
    </select>
  );
}

function RadioGroup({
  name, value, onChange, options, allowEmpty,
}: { name: string; value: string; onChange: (v: string) => void; options: OptionItem[]; allowEmpty?: string }) {
  return (
    <div className="space-y-1.5">
      {allowEmpty && (
        <label className="flex items-start gap-2 cursor-pointer text-sm text-slate-400">
          <input type="radio" name={name} checked={value === ''} onChange={() => onChange('')}
                 className="mt-0.5 accent-blue-500" />
          <span>{allowEmpty}</span>
        </label>
      )}
      {options.map(o => (
        <label key={o.id} className="flex items-start gap-2 cursor-pointer text-sm text-slate-200">
          <input type="radio" name={name} checked={value === o.value} onChange={() => onChange(o.value)}
                 className="mt-0.5 accent-blue-500" />
          <span>
            {o.label}
            {o.hint && <span className="text-[11px] text-slate-500 ml-2">{o.hint}</span>}
          </span>
        </label>
      ))}
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <fieldset className="border border-slate-800 rounded-lg p-4 bg-slate-950/30">
      <legend className="px-2 text-xs uppercase tracking-wider text-slate-400">{title}</legend>
      <div className="space-y-3">{children}</div>
    </fieldset>
  );
}

// ── Создание client-юзера ────────────────────────────────────────────────────
function AddClientModal({ orgId, onClose, onCreated }: { orgId: string; onClose: () => void; onCreated: () => void }) {
  const [form, setForm] = useState({ email: '', full_name: '' });
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [activationUrl, setActivationUrl] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true); setErr(null);
    try {
      const r = await clientsApi.create({ ...form, organization_id: orgId });
      if (r.activation_url) {
        setActivationUrl(r.activation_url);
      } else {
        onCreated();
      }
    } catch (e: any) {
      setErr(e?.response?.data?.detail || e?.message || 'Ошибка');
    } finally { setSubmitting(false); }
  };

  const copy = () => {
    if (!activationUrl) return;
    navigator.clipboard.writeText(activationUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };

  return (
    <Modal title="Новый аккаунт SD" onClose={onClose}>
      {activationUrl ? (
        <div className="space-y-3">
          <p className="text-sm text-emerald-300">Аккаунт создан.</p>
          <p className="text-xs text-slate-400">
            Перешлите клиенту эту одноразовую ссылку для установки пароля
            (действует 72 часа). После активации он войдёт в SD под своим
            email и паролем.
          </p>
          <div className="bg-slate-950 border border-slate-700 rounded px-3 py-2 text-xs font-mono text-blue-300 break-all">
            {activationUrl}
          </div>
          <div className="flex gap-2">
            <button onClick={copy}
              className="text-xs bg-slate-800 hover:bg-slate-700 text-slate-200 px-3 py-1.5 rounded">
              {copied ? 'Скопировано ✓' : 'Скопировать'}
            </button>
            <button onClick={onCreated}
              className="text-xs bg-blue-600 hover:bg-blue-500 text-white font-bold px-3 py-1.5 rounded ml-auto">
              Готово
            </button>
          </div>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-3" onClick={e => e.stopPropagation()}>
          <Field label="Email *">
            <Inp type="email" required value={form.email}
                 onChange={(e: any) => setForm(f => ({ ...f, email: e.target.value }))} />
          </Field>
          <Field label="Полное имя">
            <Inp value={form.full_name}
                 onChange={(e: any) => setForm(f => ({ ...f, full_name: e.target.value }))} />
          </Field>
          <p className="text-[11px] text-slate-500">
            Пароль клиент задаст сам по одноразовой ссылке. Email-канал доставки
            появится в следующей фазе — пока ссылка возвращается прямо сюда.
          </p>
          {err && <div className="bg-red-900/30 border border-red-800 text-red-300 px-3 py-2 rounded text-sm">{err}</div>}
          <div className="flex justify-end gap-2 pt-2 border-t border-slate-800 -mx-4 px-4 -mb-4 pb-4 mt-3">
            <button type="button" onClick={onClose} className="px-4 py-2 bg-slate-800 hover:bg-slate-700 rounded text-slate-300 text-sm">Отмена</button>
            <button type="submit" disabled={submitting || !form.email}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-500 disabled:bg-slate-700 rounded text-white text-sm font-bold">
              {submitting ? 'Создаю…' : 'Создать аккаунт'}
            </button>
          </div>
        </form>
      )}
    </Modal>
  );
}

function AddVersionModal({ orgId, instId, onClose, onCreated }: { orgId: string; instId: string; onClose: () => void; onCreated: () => void }) {
  const [form, setForm] = useState({
    component: 'backend', current: '', base: '', branch: '', commit_sha: '',
    commit_title: '', raw_text: '', note: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true); setErr(null);
    try {
      const payload: any = { ...form };
      Object.keys(payload).forEach(k => payload[k] === '' && delete payload[k]);
      await orgsApi.addVersion(orgId, instId, payload);
      onCreated();
    } catch (e: any) {
      setErr(e?.response?.data?.detail || e?.message || 'Ошибка');
    } finally { setSubmitting(false); }
  };
  const upd = (k: string, v: any) => setForm(f => ({ ...f, [k]: v }));

  return (
    <Modal title="Зафиксировать версию" onClose={onClose}>
      <form onSubmit={submit} className="space-y-3">
        <Field label="Компонент *">
          <select value={form.component} onChange={e => upd('component', e.target.value)}
            className="w-full bg-slate-950 border border-slate-700 rounded px-3 py-1.5 text-sm text-white focus:border-blue-500 outline-none">
            <option value="backend">backend</option>
            <option value="frontend">frontend</option>
            <option value="database">database</option>
            <option value="custom">custom</option>
          </select>
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Версия (current)"><Inp v={form.current} k="current" upd={upd} placeholder="v3.5.2" /></Field>
          <Field label="Базовая версия"><Inp v={form.base} k="base" upd={upd} placeholder="v3.5.0" /></Field>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Branch"><Inp v={form.branch} k="branch" upd={upd} placeholder="main" /></Field>
          <Field label="Commit SHA"><Inp v={form.commit_sha} k="commit_sha" upd={upd} placeholder="abc1234..." /></Field>
        </div>
        <Field label="Commit title (subject)"><Inp v={form.commit_title} k="commit_title" upd={upd} /></Field>
        <Field label="raw_text (если не парсится в поля)">
          <textarea value={form.raw_text} onChange={e => upd('raw_text', e.target.value)} rows={2}
            className="w-full bg-slate-950 border border-slate-700 rounded px-3 py-1.5 text-sm text-white focus:border-blue-500 outline-none" />
        </Field>
        <Field label="Заметка">
          <textarea value={form.note} onChange={e => upd('note', e.target.value)} rows={2}
            className="w-full bg-slate-950 border border-slate-700 rounded px-3 py-1.5 text-sm text-white focus:border-blue-500 outline-none" />
        </Field>
        {err && <div className="bg-red-900/30 border border-red-800 text-red-300 px-3 py-2 rounded text-sm">{err}</div>}
        <div className="flex justify-end gap-2 pt-2 border-t border-slate-800 -mx-4 px-4 -mb-4 pb-4 mt-3">
          <button type="button" onClick={onClose} className="px-4 py-2 bg-slate-800 hover:bg-slate-700 rounded text-slate-300 text-sm">Отмена</button>
          <button type="submit" disabled={submitting}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-500 disabled:bg-slate-700 rounded text-white text-sm font-bold">
            {submitting ? 'Сохраняю…' : 'Сохранить'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function Modal({ title, onClose, children }: { title: string; onClose?: () => void; children: React.ReactNode }) {
  return (
    <div className="fixed inset-0 bg-black/70 flex items-start justify-center z-50 overflow-y-auto py-10" onClick={onClose}>
      <div className="bg-slate-900 border border-slate-700 rounded-xl w-full max-w-xl mx-4 shadow-2xl" onClick={e => e.stopPropagation()}>
        <div className="p-4 border-b border-slate-800 flex justify-between">
          <h3 className="text-lg font-bold text-white">{title}</h3>
          {onClose && <button type="button" onClick={onClose} className="text-slate-400 hover:text-white">✕</button>}
        </div>
        <div className="p-4">{children}</div>
      </div>
    </div>
  );
}

function Card({ title, children }: { title: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="bg-slate-900 border border-slate-800 rounded-xl p-4">
      <div className="text-[10px] text-slate-500 uppercase tracking-wider mb-2">{title}</div>
      {children}
    </div>
  );
}

function OrgInfoTab({ org, onEdit }: { org: Organization; onEdit: () => void }) {
  /** Сводная вкладка с полной паспортной информацией об организации. */
  const fields: { label: string; value: React.ReactNode; render?: 'mono' | 'multi' }[] = [
    { label: 'Полное название', value: org.name },
    { label: 'ИНН', value: org.inn || '—', render: 'mono' },
    { label: 'КПП', value: org.kpp || '—', render: 'mono' },
    { label: 'ОГРН', value: org.ogrn || '—', render: 'mono' },
    { label: 'Юр. адрес', value: org.legal_address || '—', render: 'multi' },
    { label: 'Директор', value: org.director || '—' },
    { label: 'Отрасль', value: org.industry || '—' },
    { label: 'ОКВЭД', value: org.okved || '—', render: 'mono' },
    { label: 'Сотрудников', value: org.employees ? String(org.employees) : '—', render: 'mono' },
    { label: 'Тариф', value: org.tariff || '—' },
    { label: 'Статус проекта', value: org.project_status === 'pilot' ? 'Пилот'
      : org.project_status === 'support' ? 'Поддержка' : 'Без поддержки' },
    { label: 'Договор', value: org.contract_active ? 'Активен' : 'Не активен' },
    { label: 'Номер договора', value: org.contract_no || '—', render: 'mono' },
    { label: 'Начало действия', value: org.contract_started_at || '—', render: 'mono' },
    { label: 'Окончание', value: org.contract_ends_at || '—', render: 'mono' },
  ];

  const contacts: { label: string; value: React.ReactNode }[] = [
    { label: 'Контактное лицо', value: [org.contact_person, org.contact_position].filter(Boolean).join(' — ') || '—' },
    {
      label: 'Email',
      value: org.contact_email
        ? <a href={`mailto:${org.contact_email}`} className="text-blue-400 hover:text-blue-300">{org.contact_email}</a>
        : '—',
    },
    {
      label: 'Телефон',
      value: org.contact_phone
        ? <a href={`tel:${org.contact_phone}`} className="text-blue-400 hover:text-blue-300">
            {org.contact_phone}
            {org.contact_phone_ext && <span className="text-slate-500"> доб. {org.contact_phone_ext}</span>}
          </a>
        : '—',
    },
    {
      label: 'Telegram',
      value: org.contact_telegram
        ? <a href={`https://t.me/${org.contact_telegram.replace(/^@/, '')}`} target="_blank" rel="noreferrer" className="text-blue-400 hover:text-blue-300">{org.contact_telegram}</a>
        : '—',
    },
    {
      label: 'MAX',
      value: org.contact_max
        ? <a href={`https://max.ru/${org.contact_max.replace(/^@/, '')}`} target="_blank" rel="noreferrer" className="text-blue-400 hover:text-blue-300">{org.contact_max}</a>
        : '—',
    },
    {
      label: 'Сайт',
      value: org.website
        ? <a href={org.website.startsWith('http') ? org.website : `https://${org.website}`}
             target="_blank" rel="noreferrer" className="text-blue-400 hover:text-blue-300 inline-flex items-center gap-1">
            {org.website} <ExternalLink size={10} />
          </a>
        : '—',
    },
    { label: 'Email-домены', value: org.domains || '—', render: 'mono' as any },
  ];

  const integrations: { label: string; value: React.ReactNode }[] = [
    {
      label: 'Bitrix24',
      value: org.bitrix_deal_url
        ? <a href={org.bitrix_deal_url} target="_blank" rel="noreferrer"
             className="text-blue-400 hover:text-blue-300 inline-flex items-center gap-1">
            Открыть сделку <ExternalLink size={10} />
          </a>
        : <span className="text-slate-600">не привязана</span>,
    },
    {
      label: 'TG-чат',
      value: org.telegram_chat_url
        ? <a href={org.telegram_chat_url} target="_blank" rel="noreferrer" className="text-blue-400 hover:text-blue-300">
            {org.telegram_chat_url.slice(0, 50)}…
          </a>
        : <span className="text-slate-600">не привязан</span>,
    },
    {
      label: 'MAX-чат',
      value: org.max_chat_url
        ? <a href={org.max_chat_url} target="_blank" rel="noreferrer" className="text-blue-400 hover:text-blue-300">
            {org.max_chat_url.slice(0, 50)}…
          </a>
        : <span className="text-slate-600">не привязан</span>,
    },
    { label: 'Создана', value: <span className="font-mono text-xs">{org.created_at.slice(0, 16).replace('T', ' ')}</span> },
    { label: 'Обновлена', value: <span className="font-mono text-xs">{org.updated_at.slice(0, 16).replace('T', ' ')}</span> },
  ];

  return (
    <section className="space-y-4">
      {/* v0.9.63: компактные виджеты сверху */}
      <div className="grid md:grid-cols-2 gap-4">
        <div className="flex items-center gap-2">
          <span className="text-xs text-slate-500">Health-score:</span>
          <HealthBadge orgId={org.id} />
        </div>
        <LastActivityWidget orgId={org.id} />
      </div>

      {/* v0.9.65 #9.63: AI-категории тикетов клиента (lazy-load) */}
      <TicketCategoriesWidget orgId={org.id} />

      <div className="bg-slate-900 border border-slate-800 rounded-xl">
        <header className="flex items-center justify-between px-5 py-3 border-b border-slate-800">
          <h2 className="font-bold text-white flex items-center gap-2">
            <Info size={16} className="text-blue-400" /> Реквизиты и договор
          </h2>
          <button onClick={onEdit}
                  className="text-xs flex items-center gap-1 bg-slate-800 hover:bg-slate-700 text-slate-200 px-2.5 py-1 rounded">
            <Pencil size={11} /> Изменить
          </button>
        </header>
        <InfoGrid rows={fields} />
      </div>

      <div className="bg-slate-900 border border-slate-800 rounded-xl">
        <header className="px-5 py-3 border-b border-slate-800">
          <h2 className="font-bold text-white flex items-center gap-2">
            <Phone size={16} className="text-emerald-400" /> Контакты и web
          </h2>
        </header>
        <InfoGrid rows={contacts} />
      </div>

      <div className="bg-slate-900 border border-slate-800 rounded-xl">
        <header className="px-5 py-3 border-b border-slate-800 flex items-center justify-between flex-wrap gap-2">
          <h2 className="font-bold text-white flex items-center gap-2">
            <Globe size={16} className="text-purple-400" /> Интеграции и метаданные
          </h2>
          {/* Кнопка «Отвязать от B24» — для исправления ошибочного fuzzy-match
              в bitrix_enrich (когда например ТЕЛЕКОМ-БИРЖА сматчилась с СТЭП) */}
          <button onClick={async () => {
            if (!window.confirm(
              'Отвязать организацию от Bitrix24?\n\nbc_customer_id будет очищен. ' +
              'Это нужно когда автопоиск ошибочно связал с не той компанией ' +
              '(контакты/сделки/задачи из B24 в табе «Bitrix24» — чужие).\n\n' +
              'После отвязки автопоиск снова попробует найти при следующем sync, ' +
              'либо можно задать bc_customer_id вручную через PATCH API.'
            )) return;
            try {
              await import('../api/client').then(m =>
                m.api.post(`/api/v1/organizations/${org.id}/unlink-b24`));
              alert('Отвязано. Обновите страницу.');
              window.location.reload();
            } catch (e: any) {
              alert('Ошибка: ' + (e?.response?.data?.detail || e?.message));
            }
          }} title="Снять привязку к Customer в B24"
            className="text-xs bg-slate-800 hover:bg-rose-900/40 hover:text-rose-300 text-slate-400 px-2 py-1 rounded">
            ✕ Отвязать B24
          </button>
        </header>
        <InfoGrid rows={integrations} />
      </div>

      {(org.partner_org || org.partner_inn || org.partner_contact_name
        || org.partner_contact_phone || org.partner_contact_email) && (
        <div className="bg-slate-900 border border-slate-800 rounded-xl">
          <header className="px-5 py-3 border-b border-slate-800">
            <h2 className="font-bold text-white flex items-center gap-2">
              <Handshake size={16} className="text-amber-400" /> Партнёр
            </h2>
            <p className="text-[10px] text-slate-500 mt-1">
              Источник лида / исполнитель работ. Часто заполняется автоматически
              при «Принять в работу» из воронки BC.
            </p>
          </header>
          <InfoGrid rows={[
            { label: 'Организация', value: org.partner_org },
            { label: 'ИНН', value: org.partner_inn, render: 'mono' },
            { label: 'Контактное лицо', value: org.partner_contact_name },
            { label: 'Телефон', value: org.partner_contact_phone
              ? <a className="text-blue-300 hover:underline" href={`tel:${org.partner_contact_phone}`}>{org.partner_contact_phone}</a> : null },
            { label: 'Email', value: org.partner_contact_email
              ? <a className="text-blue-300 hover:underline" href={`mailto:${org.partner_contact_email}`}>{org.partner_contact_email}</a> : null },
          ]} />
        </div>
      )}

      {org.notes && (
        <div className="bg-slate-900 border border-slate-800 rounded-xl p-5">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-2">Заметки</div>
          <pre className="text-xs text-slate-300 whitespace-pre-wrap font-sans leading-relaxed">{org.notes}</pre>
        </div>
      )}
    </section>
  );
}

function InfoGrid({ rows }: { rows: { label: string; value: React.ReactNode; render?: 'mono' | 'multi' | any }[] }) {
  return (
    <dl className="divide-y divide-slate-800">
      {rows.map(r => (
        <div key={r.label} className="grid grid-cols-[180px_1fr] gap-3 px-5 py-2 text-sm hover:bg-slate-800/30">
          <dt className="text-slate-500">{r.label}</dt>
          <dd className={`text-slate-200 ${r.render === 'mono' ? 'font-mono text-xs' : ''} ${r.render === 'multi' ? 'whitespace-pre-wrap' : ''}`}>
            {r.value || <span className="text-slate-600">—</span>}
          </dd>
        </div>
      ))}
    </dl>
  );
}

function ClientAccountRow({ c, orgId, onChanged }: { c: ClientUser; orgId: string; onChanged: () => void }) {
  const [resetUrl, setResetUrl] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);
  const [inviteStatus, setInviteStatus] = useState<{ ok: boolean; text: string } | null>(null);

  const inviteTo = async (channel: 'tg' | 'max') => {
    setBusy(true); setInviteStatus(null);
    try {
      const r = channel === 'tg'
        ? await clientsApi.inviteToTg(orgId, c.id)
        : await clientsApi.inviteToMax(orgId, c.id);
      setInviteStatus({ ok: r.sent, text: r.message });
    } catch (e: any) {
      setInviteStatus({ ok: false, text: e?.response?.data?.detail || e?.message || 'Ошибка' });
    } finally { setBusy(false); }
  };

  const toggleActive = async () => {
    setBusy(true);
    try { await clientsApi.patch(c.id, { is_active: !c.is_active }); onChanged(); }
    finally { setBusy(false); }
  };
  const remove = async () => {
    if (!window.confirm(`Удалить аккаунт ${c.email}?`)) return;
    setBusy(true);
    try { await clientsApi.remove(c.id); onChanged(); }
    finally { setBusy(false); }
  };
  const reset = async () => {
    if (!window.confirm(`Сгенерировать новую ссылку для ${c.email}? Старый пароль перестанет работать.`)) return;
    setBusy(true);
    try {
      const r = await clientsApi.resetPassword(c.id);
      setResetUrl(r.activation_url);
    } finally { setBusy(false); }
  };
  const copy = () => {
    if (!resetUrl) return;
    navigator.clipboard.writeText(resetUrl).then(() => {
      setCopied(true); setTimeout(() => setCopied(false), 1500);
    });
  };

  return (
    <li className="bg-slate-950/40 border border-slate-800 rounded p-2">
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className={`text-xs ${c.is_active ? 'text-slate-200' : 'text-slate-500 line-through'} truncate`}>
            {c.full_name || c.email}
          </div>
          {c.position && <div className="text-[10px] text-slate-500 truncate">{c.position}</div>}
          <div className="text-[10px] text-slate-600 font-mono truncate">{c.email}</div>
          {c.phone && <div className="text-[10px] text-slate-600 truncate">{c.phone}</div>}
          {(c.telegram_username || c.max_username) && (
            <div className="text-[10px] text-slate-600 truncate flex gap-2 mt-0.5">
              {c.telegram_username && <span>TG: @{c.telegram_username.replace(/^@/, '')}</span>}
              {c.max_username && <span>MAX: @{c.max_username.replace(/^@/, '')}</span>}
            </div>
          )}
        </div>
        <div className="flex gap-1 shrink-0">
          {c.telegram_username && (
            <button onClick={() => inviteTo('tg')} disabled={busy}
                    title="Пригласить в Telegram-чат организации"
                    className="p-1 text-slate-500 hover:text-sky-400">
              <UserPlus size={11} />
              <span className="text-[8px] absolute -mt-0.5">tg</span>
            </button>
          )}
          {c.max_username && (
            <button onClick={() => inviteTo('max')} disabled={busy}
                    title="Пригласить в MAX-чат организации"
                    className="p-1 text-slate-500 hover:text-orange-400">
              <UserPlus size={11} />
              <span className="text-[8px] absolute -mt-0.5">m</span>
            </button>
          )}
          <button onClick={() => setEditing(true)} disabled={busy} title="Редактировать"
                  className="p-1 text-slate-500 hover:text-blue-400">
            <Pencil size={11} />
          </button>
          <button onClick={reset} disabled={busy} title="Сбросить пароль (новая ссылка)"
                  className="p-1 text-slate-500 hover:text-blue-400">
            <RotateCcw size={11} />
          </button>
          <button onClick={toggleActive} disabled={busy}
                  title={c.is_active ? 'Заблокировать' : 'Разблокировать'}
                  className="p-1 text-slate-500 hover:text-yellow-400">
            {c.is_active ? <Lock size={11} /> : <Unlock size={11} />}
          </button>
          <button onClick={remove} disabled={busy} title="Удалить"
                  className="p-1 text-slate-500 hover:text-red-400">
            <Trash2 size={11} />
          </button>
        </div>
      </div>
      {inviteStatus && (
        <div className={`mt-2 text-[10px] px-2 py-1 rounded ${
          inviteStatus.ok ? 'bg-emerald-900/30 border border-emerald-800 text-emerald-200'
                          : 'bg-red-900/30 border border-red-800 text-red-200'
        }`}>
          {inviteStatus.ok ? '✓ ' : '✗ '}{inviteStatus.text}
        </div>
      )}
      {resetUrl && (
        <div className="mt-2 space-y-1">
          <div className="bg-slate-950 border border-slate-700 rounded px-2 py-1 text-[10px] font-mono text-blue-300 break-all">
            {resetUrl}
          </div>
          <button onClick={copy}
                  className="text-[10px] inline-flex items-center gap-1 bg-slate-800 hover:bg-slate-700 text-slate-200 px-2 py-0.5 rounded">
            {copied ? <><Check size={10} /> Скопировано</> : <><Copy size={10} /> Скопировать</>}
          </button>
        </div>
      )}
      {editing && (
        <ClientAccountEditModal c={c}
          onClose={() => setEditing(false)}
          onSaved={() => { setEditing(false); onChanged(); }} />
      )}
    </li>
  );
}

function ClientAccountEditModal({ c, onClose, onSaved }:
  { c: ClientUser; onClose: () => void; onSaved: () => void }) {
  const [form, setForm] = useState({
    full_name: c.full_name || '',
    email: c.email,
    position: c.position || '',
    phone: c.phone || '',
    telegram_username: c.telegram_username || '',
    max_username: c.max_username || '',
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true); setErr(null);
    try {
      await clientsApi.patch(c.id, {
        full_name: form.full_name,
        email: form.email,
        position: form.position || null,
        phone: form.phone || null,
        telegram_username: form.telegram_username || null,
        max_username: form.max_username || null,
      });
      onSaved();
    } catch (e: any) {
      setErr(e?.response?.data?.detail || e?.message || 'Ошибка');
    } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/70 flex items-start justify-center z-50 overflow-y-auto py-10" onClick={onClose}>
      <div className="bg-slate-900 border border-slate-700 rounded-xl w-full max-w-md mx-4 shadow-2xl"
           onClick={e => e.stopPropagation()}>
        <header className="p-4 border-b border-slate-800 flex justify-between">
          <h3 className="text-lg font-bold text-white">Редактировать аккаунт SD</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-white">✕</button>
        </header>
        <form onSubmit={submit} className="p-4 space-y-3">
          <label className="block">
            <span className="block text-xs text-slate-400 uppercase tracking-wider mb-1">Имя</span>
            <input value={form.full_name}
                   onChange={e => setForm(f => ({ ...f, full_name: e.target.value }))}
                   className="w-full bg-slate-950 border border-slate-700 rounded px-3 py-1.5 text-sm text-white focus:border-blue-500 outline-none" />
          </label>
          <label className="block">
            <span className="block text-xs text-slate-400 uppercase tracking-wider mb-1">Должность</span>
            <input value={form.position}
                   onChange={e => setForm(f => ({ ...f, position: e.target.value }))}
                   placeholder="Например, Руководитель ИБ"
                   className="w-full bg-slate-950 border border-slate-700 rounded px-3 py-1.5 text-sm text-white focus:border-blue-500 outline-none" />
          </label>
          <label className="block">
            <span className="block text-xs text-slate-400 uppercase tracking-wider mb-1">Email (логин)</span>
            <input type="email" required value={form.email}
                   onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                   className="w-full bg-slate-950 border border-slate-700 rounded px-3 py-1.5 text-sm text-white font-mono focus:border-blue-500 outline-none" />
          </label>
          <label className="block">
            <span className="block text-xs text-slate-400 uppercase tracking-wider mb-1">Телефон</span>
            <input value={form.phone}
                   onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
                   placeholder="+7 (495) ..."
                   className="w-full bg-slate-950 border border-slate-700 rounded px-3 py-1.5 text-sm text-white focus:border-blue-500 outline-none" />
          </label>
          <label className="block">
            <span className="block text-xs text-slate-400 uppercase tracking-wider mb-1">Ник в Telegram</span>
            <input value={form.telegram_username}
                   onChange={e => setForm(f => ({ ...f, telegram_username: e.target.value }))}
                   placeholder="без @, например petrov"
                   className="w-full bg-slate-950 border border-slate-700 rounded px-3 py-1.5 text-sm text-white font-mono focus:border-blue-500 outline-none" />
          </label>
          <label className="block">
            <span className="block text-xs text-slate-400 uppercase tracking-wider mb-1">Ник в MAX</span>
            <input value={form.max_username}
                   onChange={e => setForm(f => ({ ...f, max_username: e.target.value }))}
                   placeholder="без @"
                   className="w-full bg-slate-950 border border-slate-700 rounded px-3 py-1.5 text-sm text-white font-mono focus:border-blue-500 outline-none" />
          </label>
          {err && <div className="bg-red-900/30 border border-red-800 text-red-300 px-3 py-2 rounded text-sm">{err}</div>}
          <div className="flex justify-end gap-2 pt-2 border-t border-slate-800 -mx-4 px-4 -mb-4 pb-4 mt-3">
            <button type="button" onClick={onClose} className="px-4 py-2 bg-slate-800 hover:bg-slate-700 rounded text-slate-300 text-sm">Отмена</button>
            <button type="submit" disabled={busy || !form.email}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-500 disabled:bg-slate-700 rounded text-white text-sm font-bold">
              {busy ? 'Сохраняю…' : 'Сохранить'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}


function OrgEditModal({ org, onClose, onSaved }:
  { org: Organization; onClose: () => void; onSaved: () => void }) {
  const [form, setForm] = useState({
    name: org.name, inn: org.inn, kpp: org.kpp || '',
    contact_person: org.contact_person || '', contact_position: org.contact_position || '',
    contact_phone: org.contact_phone || '', contact_phone_ext: org.contact_phone_ext || '',
    contact_email: org.contact_email || '', contact_telegram: org.contact_telegram || '',
    contact_max: org.contact_max || '',
    telegram_chat_url: org.telegram_chat_url || '', max_chat_url: org.max_chat_url || '',
    bitrix_deal_url: org.bitrix_deal_url || '',
    legal_address: org.legal_address || '', notes: org.notes || '',
    contract_no: org.contract_no || '', contract_active: org.contract_active,
    contract_started_at: org.contract_started_at || '', contract_ends_at: org.contract_ends_at || '',
    tariff: org.tariff || '',
    project_status: org.project_status as 'support' | 'pilot' | 'no_support',
    sla_plan_id: org.sla_plan_id || '',
    is_hidden: !!org.is_hidden,
    partner_org: org.partner_org || '',
    partner_inn: org.partner_inn || '',
    partner_contact_name: org.partner_contact_name || '',
    partner_contact_phone: org.partner_contact_phone || '',
    partner_contact_email: org.partner_contact_email || '',
  });
  const [plans, setPlans] = useState<SLAPlan[]>([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => { slaApi.list().then(setPlans).catch(() => {}); }, []);
  const upd = (k: string, v: any) => setForm(f => ({ ...f, [k]: v }));
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true); setErr(null);
    try {
      const payload: any = { ...form };
      // Преобразуем пустые строки в null для опциональных полей
      ['contract_started_at', 'contract_ends_at', 'sla_plan_id'].forEach(k => {
        if (payload[k] === '') payload[k] = null;
      });
      await orgsApi.update(org.id, payload);
      onSaved();
    } catch (e: any) {
      setErr(e?.response?.data?.detail || e?.message || 'Ошибка');
    } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/70 flex items-start justify-center z-50 overflow-y-auto py-8" onClick={onClose}>
      <form onSubmit={submit}
            className="bg-slate-900 border border-slate-700 rounded-xl w-full max-w-3xl mx-4 shadow-2xl"
            onClick={e => e.stopPropagation()}>
        <header className="p-4 border-b border-slate-800 flex justify-between sticky top-0 bg-slate-900 z-10">
          <h3 className="text-lg font-bold text-white">Редактировать: {org.name}</h3>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-white">✕</button>
        </header>
        <div className="p-5 grid grid-cols-2 gap-3">
          <Field label="Название *"><Inp v={form.name} k="name" upd={upd} required /></Field>
          <Field label="ИНН *">
            <div className="flex gap-1">
              <Inp v={form.inn} k="inn" upd={upd} required />
              <button type="button" onClick={async () => {
                if (!form.inn) { alert('Введите ИНН'); return; }
                try {
                  const { fnsApi } = await import('../api/v2');
                  const r = await fnsApi.lookup(form.inn);
                  upd('name', r.name || form.name);
                  if (r.kpp) upd('kpp', r.kpp);
                  if (r.legal_address) upd('legal_address', r.legal_address);
                  if (r.full_name && !form.notes) upd('notes', r.full_name);
                  alert(`Заполнено по ИНН: ${r.name}`);
                } catch (e: any) {
                  alert(e?.response?.data?.detail || 'Не удалось получить данные по ИНН');
                }
              }} title="Заполнить по ИНН из ЕГРЮЛ"
                className="text-xs bg-blue-600 hover:bg-blue-500 text-white px-3 rounded whitespace-nowrap">
                ↺ ФНС
              </button>
            </div>
          </Field>
          <Field label="КПП"><Inp v={form.kpp} k="kpp" upd={upd} /></Field>
          <Field label="Тариф"><Inp v={form.tariff} k="tariff" upd={upd} /></Field>
          <Field label="Поддержка">
            <select value={form.project_status} onChange={e => upd('project_status', e.target.value)}
              className="w-full bg-slate-950 border border-slate-700 rounded px-3 py-1.5 text-sm text-white focus:border-blue-500 outline-none">
              <option value="support">Поддержка</option>
              <option value="pilot">Пилот</option>
              <option value="no_support">Нет поддержки</option>
            </select>
          </Field>
          <Field label="SLA-план">
            <select value={form.sla_plan_id} onChange={e => upd('sla_plan_id', e.target.value)}
              className="w-full bg-slate-950 border border-slate-700 rounded px-3 py-1.5 text-sm text-white focus:border-blue-500 outline-none">
              <option value="">— по умолчанию —</option>
              {plans.map(p => <option key={p.id} value={p.id}>{p.name}{p.is_default ? ' (default)' : ''}</option>)}
            </select>
          </Field>
          <Field label="Контактное лицо"><Inp v={form.contact_person} k="contact_person" upd={upd} /></Field>
          <Field label="Должность"><Inp v={form.contact_position} k="contact_position" upd={upd} /></Field>
          <Field label="Телефон"><Inp v={form.contact_phone} k="contact_phone" upd={upd} /></Field>
          <Field label="Доб."><Inp v={form.contact_phone_ext} k="contact_phone_ext" upd={upd} /></Field>
          <Field label="Email"><Inp v={form.contact_email} k="contact_email" upd={upd} type="email" /></Field>
          <Field label="Telegram"><Inp v={form.contact_telegram} k="contact_telegram" upd={upd} /></Field>
          <Field label="MAX (ник)"><Inp v={form.contact_max} k="contact_max" upd={upd} /></Field>
          <span />
          <Field label="Чат в Telegram (URL)"><Inp v={form.telegram_chat_url} k="telegram_chat_url" upd={upd} /></Field>
          <Field label="Чат в MAX (URL)"><Inp v={form.max_chat_url} k="max_chat_url" upd={upd} /></Field>
          <Field label="Сделка в Bitrix24 (URL)"><Inp v={form.bitrix_deal_url} k="bitrix_deal_url" upd={upd} /></Field>
          <Field label="№ договора"><Inp v={form.contract_no} k="contract_no" upd={upd} /></Field>
          <Field label="Договор активен">
            <label className="flex items-center gap-2 mt-2 text-sm text-slate-300">
              <input type="checkbox" checked={form.contract_active}
                     onChange={e => upd('contract_active', e.target.checked)}
                     className="accent-blue-500" />
              Действует
            </label>
          </Field>
          <Field label="Видимость">
            <label className="flex items-center gap-2 mt-2 text-sm text-slate-300" title="Скрыть из списка организаций и из ассайнов тикетов">
              <input type="checkbox" checked={form.is_hidden}
                     onChange={e => upd('is_hidden', e.target.checked)}
                     className="accent-blue-500" />
              Скрыть из списка
            </label>
          </Field>
          <Field label="С"><Inp v={form.contract_started_at} k="contract_started_at" upd={upd} type="date" /></Field>
          <Field label="До"><Inp v={form.contract_ends_at} k="contract_ends_at" upd={upd} type="date" /></Field>
          <label className="block col-span-2">
            <span className="block text-xs text-slate-400 uppercase tracking-wider mb-1">Юр. адрес</span>
            <Inp v={form.legal_address} k="legal_address" upd={upd} />
          </label>
          <label className="block col-span-2">
            <span className="block text-xs text-slate-400 uppercase tracking-wider mb-1">Заметки</span>
            <textarea value={form.notes} onChange={e => upd('notes', e.target.value)} rows={2}
              className="w-full bg-slate-950 border border-slate-700 rounded px-3 py-1.5 text-sm text-white focus:border-blue-500 outline-none" />
          </label>

          <div className="col-span-2 mt-2 border-t border-slate-800 pt-3">
            <div className="text-xs uppercase tracking-wider text-amber-400 font-bold mb-2 flex items-center gap-2">
              <Handshake size={14} /> Партнёр
            </div>
            <p className="text-[10px] text-slate-500 mb-2">
              Источник лида / исполнитель работ. Заполняется автоматически при
              миграции из BC.Pilot, либо вручную здесь.
            </p>
          </div>
          <Field label="Организация-партнёр"><Inp v={form.partner_org} k="partner_org" upd={upd} /></Field>
          <Field label="ИНН партнёра"><Inp v={form.partner_inn} k="partner_inn" upd={upd} /></Field>
          <Field label="Контактное лицо (партнёр)"><Inp v={form.partner_contact_name} k="partner_contact_name" upd={upd} /></Field>
          <Field label="Телефон (партнёр)"><Inp v={form.partner_contact_phone} k="partner_contact_phone" upd={upd} /></Field>
          <Field label="Email (партнёр)"><Inp v={form.partner_contact_email} k="partner_contact_email" upd={upd} type="email" /></Field>
        </div>
        {err && <div className="bg-red-900/30 border-y border-red-800 text-red-300 px-4 py-2 text-sm">{err}</div>}
        <div className="p-4 flex justify-end gap-2 border-t border-slate-800 sticky bottom-0 bg-slate-900">
          <button type="button" onClick={onClose} className="px-4 py-2 bg-slate-800 hover:bg-slate-700 rounded text-slate-300 text-sm">Отмена</button>
          <button type="submit" disabled={busy}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-500 disabled:bg-slate-700 rounded text-white text-sm font-bold">
            {busy ? 'Сохраняю…' : 'Сохранить'}
          </button>
        </div>
      </form>
    </div>
  );
}

function SLACard({ orgId }: { orgId: string }) {
  const [plan, setPlan] = useState<SLAPlan | null>(null);
  useEffect(() => {
    slaApi.forOrg(orgId).then(setPlan).catch(() => setPlan(null));
  }, [orgId]);
  if (!plan) return null;
  const rows: Array<[string, number, number]> = [
    ['Срочно (P1)', plan.frt_p1_min, plan.ttr_p1_min],
    ['Важно (P2)',  plan.frt_p2_min, plan.ttr_p2_min],
    ['Обычный (P3)', plan.frt_p3_min, plan.ttr_p3_min],
    ['Не срочно (P4)', plan.frt_p4_min, plan.ttr_p4_min],
  ];
  return (
    <Card title={<span>SLA-план: {plan.name}{plan.is_default && ' (по умолчанию)'}</span>}>
      <table className="w-full text-[11px]">
        <thead>
          <tr className="text-slate-500">
            <th className="text-left font-normal pb-1"></th>
            <th className="text-right font-normal pb-1">FRT</th>
            <th className="text-right font-normal pb-1">TTR</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(([lbl, frt, ttr]) => (
            <tr key={lbl}>
              <td className="text-slate-300 py-0.5">{lbl}</td>
              <td className="text-right text-slate-200 font-mono">{fmtMinutes(frt)}</td>
              <td className="text-right text-slate-200 font-mono">{fmtMinutes(ttr)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="text-[10px] text-slate-600 mt-2">
        FRT — первый ответ, TTR — решение тикета. {plan.working_hours_24x7 ? '24×7' : 'рабочие часы'}.
      </p>
    </Card>
  );
}

function TabBtn({
  active, onClick, icon, label, count,
}: { active: boolean; onClick: () => void; icon: React.ReactNode; label: string; count?: number }) {
  return (
    <button onClick={onClick}
      className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition -mb-px ${
        active
          ? 'border-blue-500 text-white bg-slate-900'
          : 'border-transparent text-slate-400 hover:text-white hover:bg-slate-900/50'
      }`}>
      {icon}
      {label}
      {count !== undefined && (
        <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${
          active ? 'bg-blue-600 text-white' : 'bg-slate-800 text-slate-400'
        }`}>{count}</span>
      )}
    </button>
  );
}

function Field({ label, children }: any) {
  return (<label className="block">
    <span className="block text-xs text-slate-400 uppercase tracking-wider mb-1">{label}</span>
    {children}
  </label>);
}
function Inp(props: any) {
  const { v, k, upd, ...rest } = props;
  if (k && upd) {
    return <input value={v ?? ''} onChange={e => upd(k, e.target.value)}
      className="w-full bg-slate-950 border border-slate-700 rounded px-3 py-1.5 text-sm text-white focus:border-blue-500 outline-none" {...rest} />;
  }
  return <input
    className="w-full bg-slate-950 border border-slate-700 rounded px-3 py-1.5 text-sm text-white focus:border-blue-500 outline-none" {...rest} />;
}


function OrgAnalyticsTab({ orgId }: { orgId: string }) {
  const [days, setDays] = useState(30);
  const [data, setData] = useState<any | null>(null);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    setLoading(true);
    import('../api/v2').then(({ orgExtraApi }) =>
      orgExtraApi.analytics(orgId, days).then(setData).finally(() => setLoading(false))
    );
  }, [orgId, days]);
  if (loading || !data) return <Loader2 className="animate-spin text-blue-400 my-8" />;
  const human = (m: number | null) => m == null ? '—' :
    m < 60 ? `${Math.round(m)} мин` :
    m < 60*24 ? `${(m/60).toFixed(1)} ч` :
    `${(m/60/24).toFixed(1)} дн`;
  return (
    <section className="bg-slate-900 border border-slate-800 rounded-xl p-5">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-bold text-white">Отчёт по взаимодействию с поддержкой</h2>
        <div className="flex gap-1">
          {[7, 14, 30, 60, 90, 180, 365].map(d => (
            <button key={d} onClick={() => setDays(d)}
              className={`px-2 py-1 rounded text-[10px] font-bold uppercase ${
                days === d ? 'bg-blue-600 text-white' : 'bg-slate-800 text-slate-400 hover:text-white'
              }`}>{d}д</button>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <Tile2 label="Создано" value={data.totals.created} />
        <Tile2 label="Решено" value={data.totals.resolved} />
        <Tile2 label="Breach FRT" value={data.totals.breach_frt} color="text-orange-300" />
        <Tile2 label="Breach TTR" value={data.totals.breach_ttr} color="text-red-300" />
        <Tile2 label="ср. FRT" value={human(data.avg_frt_minutes)} />
        <Tile2 label="ср. TTR" value={human(data.avg_ttr_minutes)} />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <DistBox title="По приоритетам" data={data.by_priority} />
        <DistBox title="По линиям" data={data.by_line} />
        <DistBox title="По статусам" data={data.by_status} />
      </div>
      {data.by_agent.length > 0 && (
        <div className="mt-4">
          <h3 className="text-xs uppercase tracking-wider text-slate-400 mb-2">Агенты по нагрузке</h3>
          <ul className="text-sm text-slate-200 divide-y divide-slate-800">
            {data.by_agent.map((a: any) => (
              <li key={a.user_id} className="flex justify-between py-1.5">
                <span>{a.name}</span><span className="font-mono text-emerald-400">{a.count}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}

function Tile2({ label, value, color }: { label: string; value: any; color?: string }) {
  return (
    <div className="bg-slate-950/40 border border-slate-800 rounded-lg p-3">
      <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className={`text-xl font-bold ${color || 'text-white'}`}>{value}</div>
    </div>
  );
}

function DistBox({ title, data }: { title: string; data: Record<string, number> }) {
  const max = Math.max(1, ...Object.values(data));
  return (
    <div className="bg-slate-950/40 border border-slate-800 rounded-lg p-3">
      <div className="text-xs text-slate-400 mb-2">{title}</div>
      <ul className="space-y-1.5">
        {Object.entries(data).map(([k, v]) => (
          <li key={k}>
            <div className="flex items-center justify-between text-[11px] mb-0.5">
              <span className="text-slate-300">{k}</span>
              <span className="font-mono text-slate-400">{v}</span>
            </div>
            <div className="h-1 bg-slate-800 rounded overflow-hidden">
              <div className="h-full bg-blue-500" style={{ width: `${(v / max) * 100}%` }} />
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}


function ChatLinksRow({ orgId }: { orgId: string }) {
  const [links, setLinks] = useState<ChatLinks | null>(null);
  useEffect(() => {
    let alive = true;
    chatLinksApi.get(orgId).then(l => { if (alive) setLinks(l); }).catch(() => {});
    return () => { alive = false; };
  }, [orgId]);

  const open = (ch: 'tg' | 'max') => {
    const c = ch === 'tg' ? links?.tg : links?.max;
    if (!c) return;
    if (c.invite_url) {
      window.open(c.invite_url, '_blank', 'noreferrer');
      return;
    }
    const label = ch === 'tg' ? 'Telegram' : 'MAX';
    const reason = c.reason || 'не известна причина';
    if (!c.linked) {
      alert(`Чат в ${label} не подключён к этой организации.\n\n` +
            `${reason}.\n\nПодключить можно в карточке «${label}-чат» справа: ` +
            `получи код, добавь бота в группу и пришли /start CODE.`);
    } else {
      alert(`Чат в ${label} подключён, но получить прямую ссылку не удалось.\n\n` +
            `${reason}.\n\nЗадайте ссылку вручную: «Изменить» → «Чат в ${label} (URL)».`);
    }
  };

  return (
    <div className="grid grid-cols-2 gap-2">
      <button onClick={() => open('tg')}
        className={`text-xs inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded border transition ${
          links?.tg.invite_url
            ? 'bg-sky-900/30 border-sky-700 text-sky-200 hover:bg-sky-900/60'
            : 'bg-slate-900 border-slate-800 text-slate-500 hover:bg-slate-800'
        }`}>
        <Send size={11} /> Чат в Telegram {links?.tg.invite_url && <ExternalLink size={9} />}
      </button>
      <button onClick={() => open('max')}
        className={`text-xs inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded border transition ${
          links?.max.invite_url
            ? 'bg-orange-900/30 border-orange-700 text-orange-200 hover:bg-orange-900/60'
            : 'bg-slate-900 border-slate-800 text-slate-500 hover:bg-slate-800'
        }`}>
        <MessageSquare size={11} /> Чат в MAX {links?.max.invite_url && <ExternalLink size={9} />}
      </button>
    </div>
  );
}
